import React, { useRef, useState, useEffect, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { database } from '../../utils/firebase';
import { ref, get, set, onValue } from 'firebase/database';
import { SettingsContext } from '../../contexts/settings-provider';
import styles from './Play.module.scss';

import Loading from '../../components/Loading/Loading';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import Form from '../../components/Form/Form';
import Main from '../../components/Main/Main';
import Heading from '../../components/Heading/Heading';
import Text from '../../components/Text/Text';
import Section from '../../components/Section/Section';
import stringToColour from '../../utils/stringToColor';
import classNames from 'classnames';

const Play = () => {
  const nameMinLength = 1;

  const location = useLocation();
  const [settings] = useContext(SettingsContext);

  const [loading, setLoading] = useState(true);
  const [roomCode] = useState(location.search.replace('?code=', ''));
  const [invalidRoom, setInvalidRoom] = useState(false);
  const [validRooom, setValidRoom] = useState(false);

  const [registered, setRegistered] = useState(false);
  const [name, setName] = useState('');
  const [ready, setReady] = useState(false);

  const [playing, setPlaying] = useState(false);
  const [health, setHealth] = useState(100);
  const [dead, setDead] = useState(false);
  const [orientation, setOrientation] = useState(0);
  const [shooting, setShooting] = useState(false);

  const gameRef = useRef(null);
  const lastUpdate = useRef(Date.now());

  // Check that user is signed in and that the room exists and if user is registered
  useEffect(() => {
    if (settings.user) {
      if (roomCode) {
        const roundData = ref(database, `games/${roomCode}/`);
        get(roundData)
          .then((snapshot) => {
            if (snapshot.exists()) {
              const registeredName =
                snapshot.val()?.users?.[settings.user.uid]?.name;
              if (registeredName) {
                setRegistered(true);
                setName(registeredName);
              } else {
                setRegistered(false);
              }
              setReady(false);
              setLoading(false);
              setValidRoom(true);
            } else {
              setInvalidRoom(true);
            }
          })
          .catch((error) => {
            setInvalidRoom(true);
          });
      } else {
        setInvalidRoom(true);
      }
    }
  }, [roomCode, settings.user]);

  // Register user
  const registerUser = () => {
    if (name.length >= nameMinLength) {
      const userRef = ref(
        database,
        `games/${roomCode}/users/${settings.user.uid}`
      );
      set(userRef, { name: name, ready: false });
      setRegistered(true);
    }
  };

  // Update user ready state
  useEffect(() => {
    if (validRooom && registered) {
      const readyRef = ref(
        database,
        `games/${roomCode}/users/${settings.user.uid}/ready`
      );
      set(readyRef, ready);
    }
  }, [validRooom, registered, ready, roomCode, settings.user]);

  // Get and handle orientation changes
  const handleOrientation = (event) => {
    const newOrientation = Math.round(event.gamma) / 90;
    setOrientation(Math.round(newOrientation * 100));
  };

  const getOrientationPermission = () => {
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      DeviceMotionEvent.requestPermission()
        .then((state) => {
          if (state === 'granted') {
            window.addEventListener('deviceorientation', handleOrientation);
            setReady(true);
          } else {
            setReady(false);
          }
        })
        .catch(console.error);
    } else {
      window.addEventListener('deviceorientation', handleOrientation);
      setReady(true);
    }
    window.screen.orientation.lock('portrait-primary');
  };

  // Get and handle shooting changes
  useEffect(() => {
    const startShooting = (e) => {
      //e.preventDefault();
      setShooting(true);
    };
    const endShooting = (e) => {
      //e.preventDefault();
      setShooting(false);
    };
    window.addEventListener('pointerdown', startShooting);
    window.addEventListener('pointerup', endShooting);
    return () => {
      window.removeEventListener('pointerdown', startShooting);
      window.removeEventListener('pointerup', endShooting);
    };
  }, [playing]);

  // Listen for data on room
  useEffect(() => {
    if (registered) {
      const roundData = ref(
        database,
        `games/${roomCode}/users/${settings.user.uid}/banned`
      );
      onValue(roundData, (snapshot) => {
        const data = snapshot.val();
        if (data === true) {
          setInvalidRoom(true);
        }
      });
    }
  }, [registered, settings.user, roomCode]);

  // Listen for data on room status
  useEffect(() => {
    if (registered) {
      const roundData = ref(database, `games/${roomCode}/round`);
      onValue(roundData, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setPlaying(data.playing ? true : false);
        } else {
          setInvalidRoom(true);
        }
      });
    }
  }, [registered, roomCode]);

  // Listen for data on room status
  useEffect(() => {
    if (registered) {
      const roundData = ref(
        database,
        `games/${roomCode}/health/${settings.user.uid}`
      );
      onValue(roundData, (snapshot) => {
        const data = snapshot.val();
        if (!isNaN(data)) {
          setHealth(data);
        }
      });
    }
  }, [registered, roomCode, settings.user]);

  useEffect(() => {
    if (health <= 0) {
      setDead(true);
    } else {
      setDead(false);
    }
  }, [health]);

  // Updated orientation
  useEffect(() => {
    if (ready && playing && !dead) {
      const userRef = ref(
        database,
        `games/${roomCode}/controls/${settings.user.uid}/orientation`
      );
      if (Date.now() - lastUpdate.current > 250) {
        set(userRef, orientation);
        lastUpdate.current = Date.now();
      }
    }
  }, [dead, ready, playing, roomCode, orientation, settings.user]);

  // Updated shooting
  useEffect(() => {
    if (ready && playing && !dead) {
      const userRef = ref(
        database,
        `games/${roomCode}/controls/${settings.user.uid}/shooting`
      );
      set(userRef, shooting);
    }
  }, [dead, ready, playing, roomCode, shooting, settings.user]);

  if (invalidRoom) {
    return <Navigate to={'/'} state={{ error: 'Invalid room' }} />;
  } else if (loading) {
    return <Loading />;
  } else if (!registered) {
    return (
      <Main>
        <Form>
          <Heading>Whats your name?</Heading>
          <Input max={12} onChange={setName} placeholder={`Display name`} />
          <Button onClick={registerUser} disabled={name.length < nameMinLength}>
            Join game
          </Button>
        </Form>
      </Main>
    );
  } else if (!ready) {
    return (
      <Main>
        <Section>
          <Heading>We need acces to your gyroscope</Heading>
          <Text>This game is controlled using your gyroscope</Text>
          <Button
            onClick={() => {
              getOrientationPermission();
            }}
          >
            Grant access
          </Button>
        </Section>
      </Main>
    );
  } else if (playing === false) {
    return (
      <Main>
        <Section>
          <Heading>Hi, {name}!</Heading>
          <Text>Waiting for the host to start the game</Text>
        </Section>
      </Main>
    );
  } else if (playing === true) {
    return (
      <div className={styles.game}>
        {dead && <div>You died</div>}
        {!dead && (
          <div className={styles.bar} ref={gameRef}>
            <div
              className={classNames(styles.orientation, {
                [styles.shooting]: shooting === true,
              })}
              style={{
                transform: `translate3d(${orientation / 2.5}vw, 0px, 10px)`,
              }}
            />
            <div
              className={styles.fill}
              style={{
                backgroundColor: stringToColour(settings.user.uid),
                height: `${health}%`,
              }}
            />
          </div>
        )}
      </div>
    );
  }
};

export default Play;
